import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatIDR } from "../lib/format";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";
import PondokIcon from "../components/PondokIcon";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }) : "—";

export default function InvoicePrint() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get(`/custom-invoices/${id}`), api.get("/settings")])
      .then(([r, s]) => { setInv(r.data); setSettings(s.data); })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-12 text-center text-[#586D61]">Memuat…</div>;
  if (!inv) return <div className="p-12 text-center text-[#D14D4D]">Invoice tidak ditemukan</div>;

  const isEN = inv.language === "en";
  const t = (id, en) => isEN ? en : id;

  const subtotal = (inv.items || []).reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
  const total = subtotal - (inv.discount || 0);
  const dep = inv.status === "Paid" ? total : (inv.deposit || 0);
  const outstanding = Math.max(0, total - dep);

  return (
    <div className="bg-[#FDFBF7] min-h-screen">
      <div className="print:hidden bg-white border-b border-[#EBE6DE] px-6 py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm">
        <Link to="/invoices" className="inline-flex items-center gap-2 text-sm text-[#586D61] hover:text-[#082618]">
          <ArrowLeft className="h-4 w-4" /> {t("Kembali ke Invoice", "Back to Invoices")}
        </Link>
        <Button data-testid="btn-print-invoice" onClick={() => window.print()} className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl gap-2">
          <Printer className="h-4 w-4" /> {t("Cetak / Save PDF", "Print / Save PDF")}
        </Button>
      </div>

      <div className="max-w-3xl mx-auto p-8 print:p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b-2 border-[#082618] pb-6 mb-8">
          <div className="flex items-center gap-3">
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt="Logo" className="h-14 w-14 rounded-xl object-contain bg-white border border-[#EBE6DE]" data-testid="invoice-logo" />
            ) : (
              <div className="h-14 w-14 rounded-xl bg-[#D16B4D] flex items-center justify-center">
                <PondokIcon className="h-8 w-8 text-white" strokeWidth={1.8} />
              </div>
            )}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D16B4D]">PondokRaja</div>
              <div className="font-heading text-2xl font-bold text-[#172E21] leading-tight">
                {settings?.homestay_name || "Meos Ambower Homestay"}
              </div>
              {settings?.homestay_address && <div className="text-xs text-[#586D61] mt-0.5">{settings.homestay_address}</div>}
              <div className="text-xs text-[#586D61] mt-0.5 font-mono">
                {settings?.homestay_email}{settings?.homestay_phone ? ` · ${settings.homestay_phone}` : ""}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D16B4D]">INVOICE</div>
            <div className="font-heading text-2xl font-bold text-[#172E21]">{inv.invoice_number}</div>
            <div className="text-xs text-[#586D61] mt-1">{fmtDate(inv.invoice_date)}</div>
            <div className={`mt-2 inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
              inv.status === "Paid" ? "bg-emerald-100 text-emerald-700" :
              inv.status === "Sent" ? "bg-blue-100 text-blue-700" :
              inv.status === "Cancelled" ? "bg-rose-100 text-rose-700" :
              "bg-stone-100 text-stone-700"
            }`}>{inv.status}</div>
          </div>
        </div>

        {/* Bill to */}
        <div className="mb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">{t("Ditagihkan kepada", "Bill to")}</div>
          <div className="font-heading text-xl font-medium text-[#172E21] mt-0.5">{inv.guest_name}</div>
          {inv.phone_number && <div className="text-sm text-[#586D61] font-mono">{inv.phone_number}</div>}
          {inv.country && <div className="text-sm text-[#586D61]">{inv.country}</div>}
        </div>

        {/* Items table */}
        <table className="w-full text-sm border-t-2 border-[#082618] mb-6">
          <thead>
            <tr className="bg-[#FDFBF7] border-b border-[#EBE6DE]">
              <th className="text-left px-3 py-2.5 font-bold text-[#172E21]">{t("Deskripsi", "Description")}</th>
              <th className="text-center px-3 py-2.5 font-bold text-[#172E21] w-20">{t("Qty", "Qty")}</th>
              <th className="text-right px-3 py-2.5 font-bold text-[#172E21] w-32">{t("Harga", "Price")}</th>
              <th className="text-right px-3 py-2.5 font-bold text-[#172E21] w-36">{t("Jumlah", "Amount")}</th>
            </tr>
          </thead>
          <tbody>
            {(inv.items || []).map((it, idx) => (
              <tr key={idx} className="border-b border-[#EBE6DE]/50">
                <td className="px-3 py-3 text-[#172E21]">{it.description}</td>
                <td className="px-3 py-3 text-center font-mono">{it.quantity}</td>
                <td className="px-3 py-3 text-right font-mono">{formatIDR(it.unit_price)}</td>
                <td className="px-3 py-3 text-right font-mono font-bold">{formatIDR((it.quantity || 0) * (it.unit_price || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-full md:w-1/2 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-[#586D61]">{t("Subtotal", "Subtotal")}</span>
              <span className="font-mono">{formatIDR(subtotal)}</span>
            </div>
            {inv.discount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-[#586D61]">{t("Diskon", "Discount")}</span>
                <span className="font-mono text-[#D14D4D]">-{formatIDR(inv.discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold pt-2 border-t border-[#082618]">
              <span className="text-[#172E21]">{t("Total", "Total")}</span>
              <span className="font-mono text-[#D16B4D]">{formatIDR(total)}</span>
            </div>
            {dep > 0 && (
              <>
                <div className="flex justify-between text-sm pt-1">
                  <span className="text-[#586D61]">{t("DP Diterima", "Deposit Paid")}</span>
                  <span className="font-mono text-[#2C7A51]">{formatIDR(dep)}</span>
                </div>
                <div className="flex justify-between text-base font-bold pt-2 border-t border-[#EBE6DE]">
                  <span className="text-[#172E21]">{t("Sisa Tagihan", "Balance Due")}</span>
                  <span className={`font-mono ${outstanding > 0 ? "text-[#D99338]" : "text-[#2C7A51]"}`}>
                    {outstanding > 0 ? formatIDR(outstanding) : t("LUNAS", "PAID")}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {inv.notes && (
          <div className="mb-6 border-l-4 border-[#D16B4D] pl-4 py-2 bg-[#FDFBF7]/60 rounded-r-lg">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">{t("Catatan", "Notes")}</div>
            <div className="text-sm text-[#172E21] mt-1">{inv.notes}</div>
          </div>
        )}

        {/* Bank Info */}
        {(settings?.bank_name || settings?.bank_account_number || settings?.bank_info) && outstanding > 0 && (
          <div className="mb-6 border border-[#082618] rounded-xl p-4 bg-[#FDFBF7]" data-testid="invoice-bank-info">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#082618] mb-2">
              {t("Informasi Pembayaran", "Payment Information")}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              {settings.bank_name && (
                <div>
                  <div className="text-[10px] uppercase text-[#586D61]">{t("Bank", "Bank")}</div>
                  <div className="font-medium text-[#172E21]">{settings.bank_name}</div>
                </div>
              )}
              {settings.bank_account_number && (
                <div>
                  <div className="text-[10px] uppercase text-[#586D61]">{t("No. Rekening", "Account No.")}</div>
                  <div className="font-mono font-bold text-[#172E21]">{settings.bank_account_number}</div>
                </div>
              )}
              {settings.bank_account_name && (
                <div>
                  <div className="text-[10px] uppercase text-[#586D61]">{t("Atas Nama", "Account Name")}</div>
                  <div className="font-medium text-[#172E21]">{settings.bank_account_name}</div>
                </div>
              )}
            </div>
            {settings.bank_info && (
              <div className="text-xs text-[#586D61] mt-3 pt-3 border-t border-[#EBE6DE] whitespace-pre-line">{settings.bank_info}</div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 grid grid-cols-2 gap-12 print:break-inside-avoid">
          <div>
            <div className="text-xs text-[#586D61] mb-1">{t("Terima kasih atas kepercayaan Anda.", "Thank you for your trust.")}</div>
            <div className="text-xs text-[#586D61]">{t("Pembayaran via transfer bank atau cash.", "Payment via bank transfer or cash.")}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-[#586D61] mb-12">{t("Hormat kami,", "Sincerely,")}</div>
            <div className="border-t border-[#082618] pt-2">
              <div className="text-xs font-medium text-[#172E21]">{settings?.homestay_name || "Pemilik Homestay"}</div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-[10px] text-[#586D61]/60 print:fixed print:bottom-4 print:left-0 print:right-0">
          Generated by PondokRaja · pondokraja.com
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 14mm; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}
